"use client";

import { useEffect } from "react";

type ScreenWakeLock = {
  request: (type: "screen") => Promise<{ release: () => Promise<void> }>;
};

function screenWakeLock(): ScreenWakeLock | null {
  if (typeof navigator === "undefined") {
    return null;
  }
  const candidate = (navigator as Navigator & { wakeLock?: ScreenWakeLock })
    .wakeLock;
  return candidate ?? null;
}

/**
 * Asks the browser to keep the screen on. Safe to call from a click as well
 * as from the hook, because some browsers only grant the lock after a gesture.
 */
export async function requestScreenWakeLock(): Promise<void> {
  const wakeLock = screenWakeLock();
  if (!wakeLock) {
    return;
  }
  try {
    await wakeLock.request("screen");
  } catch {
    // Denied, unsupported, or the page is hidden. The display still works.
  }
}

export function useScreenWakeLock(enabled = true): void {
  useEffect(() => {
    if (!enabled || !screenWakeLock()) {
      return;
    }

    let released = false;
    let sentinel: { release: () => Promise<void> } | null = null;

    const acquire = async () => {
      const wakeLock = screenWakeLock();
      if (!wakeLock || released || document.visibilityState === "hidden") {
        return;
      }
      try {
        const next = await wakeLock.request("screen");
        if (released) {
          await next.release();
          return;
        }
        const previous = sentinel;
        sentinel = next;
        if (previous) {
          await previous.release().catch(() => undefined);
        }
      } catch {
        // The screen may still sleep. Full screen is a second chance to ask.
      }
    };

    void acquire();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      const current = sentinel;
      sentinel = null;
      if (current) {
        void current.release();
      }
    };
  }, [enabled]);
}
